import React, { Fragment } from 'react';
import { NarvarStatus } from '../../../../../__generated__/loma';
import {
  DeviceLayout,
  ViewLayout,
  RowLayout,
  ColLayout,
  Feature,
  ConditionType,
  TreeNodeTypes,
  DEFAULT_NARVAR_STATUS,
  ConditionOperator,
} from './layoutTree';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import renderLayout, { LayoutNodeRenderers } from './renderLayout';
import { Column, ColumnNumbers, Container } from '../../ui/Grid';

const defaultViewRenderProp: LayoutNodeRenderers['view'] = (options) => {
  return <Container>{options?.children}</Container>;
};

const defaultRowRenderProp: LayoutNodeRenderers['row'] = (options) => {
  return <Fragment>{options?.children}</Fragment>;
};

const defaultColRenderProp: LayoutNodeRenderers['col'] = (options) => {
  const span = (options?.node?.width?.toString() as ColumnNumbers) ?? '12';
  return <Column span={span}>{options?.children}</Column>;
};

export interface LayoutComponentProps
  extends Partial<Pick<LayoutNodeRenderers, 'col' | 'row' | 'view'>>,
    Pick<LayoutNodeRenderers, 'feature'> {
  deviceLayout: DeviceLayout;
}

type LayoutNodeType = ViewLayout | RowLayout | ColLayout | Feature;

const evaluateCondition: (
  conditionOperand: string | string[],
  operator: ConditionOperator | undefined,
  value: string,
) => boolean = (conditionOperand, operator, value) => {
  switch (operator) {
    case ConditionOperator.NEQ:
      return conditionOperand !== value;
    case ConditionOperator.IN:
      if (Array.isArray(conditionOperand)) {
        return conditionOperand.includes(value);
      }
      // eslint-disable-next-line no-console
      console.error(
        `Array operand was not specified for IN operator: ${JSON.stringify({
          conditionOperand,
          operator,
          value,
        })}`,
      );
      return false;
    case ConditionOperator.EQ:
    default:
      return conditionOperand === value;
  }
};

const filterLayoutForConditions: <T extends LayoutNodeType>(
  node: T,
  status: NarvarStatus,
  subStatus: string,
  carrier: string,
  category: string,
) => T = (node, status, subStatus, carrier, category) => {
  const newNode = { ...node };
  if (newNode.type === TreeNodeTypes.ROW) {
    newNode.children = newNode.children.map((child) =>
      filterLayoutForConditions<(typeof newNode.children)[0]>(
        child,
        status,
        subStatus,
        carrier,
        category,
      ),
    );
    return newNode;
  } else if (newNode.type === TreeNodeTypes.COL) {
    newNode.children = newNode.children.filter((child) => {
      // only put features in the renderable layout that meet all
      // their conditions or don't have conditions (undefined).
      if (child.type === TreeNodeTypes.FEATURE) {
        return (
          child.options?.conditions?.every((condition) => {
            if (condition.type === ConditionType.SHIPMENT_STATUS) {
              return evaluateCondition(
                condition.status,
                condition.operator,
                status,
              );
            }
            if (condition.type === ConditionType.SUB_STATUS) {
              return evaluateCondition(
                condition.subStatus,
                condition.operator,
                subStatus,
              );
            }
            if (condition.type === ConditionType.CARRIER_MONIKER) {
              return evaluateCondition(
                condition.carrierMoniker,
                condition.operator,
                carrier,
              );
            }
            if (condition.type === ConditionType.CATEGORY) {
              return evaluateCondition(
                condition.category,
                condition.operator,
                category,
              );
            }
            return true;
          }) !== false
        );
      } else {
        return filterLayoutForConditions<(typeof newNode.children)[0]>(
          child,
          status,
          subStatus,
          carrier,
          category,
        );
      }
    });
  } else if (newNode.type === TreeNodeTypes.VIEW) {
    newNode.children = newNode.children.map((child) =>
      filterLayoutForConditions<(typeof newNode.children)[0]>(
        child,
        status,
        subStatus,
        carrier,
        category,
      ),
    );
  }

  return newNode;
};

const LayoutComponent = ({
  deviceLayout,
  view = defaultViewRenderProp,
  row = defaultRowRenderProp,
  col = defaultColRenderProp,
  feature,
}: LayoutComponentProps) => {
  const {
    state: { carrier, trackingStatus, category, trackingStatusCode },
  } = usePageContext();

  if (!deviceLayout) return null;

  const filteredLayout = filterLayoutForConditions(
    deviceLayout,
    trackingStatus ?? DEFAULT_NARVAR_STATUS,
    trackingStatusCode ?? '',
    carrier ?? '',
    category ?? '',
  );

  return renderLayout({
    node: filteredLayout,
    view,
    row,
    col,
    feature,
  });
};

export default LayoutComponent;
